/**
 * [#663] The four states every portal screen owes the user (design brief §5:
 * "every page needs empty, loading, error, and permission-denied states;
 * empty states should teach the workflow").
 *
 * Built once here so slices 4–7 render the same states rather than each
 * inventing its own. Styling is the ported kit vocabulary only (var(--nu-*)
 * + .nu-* classes) — no second visual language.
 *
 * The permission-denied state is a DESIGNED screen, not an error page and not
 * a silent redirect: it names the area, states plainly that the tenant's
 * organisation has not been granted it, and offers the granted areas the user
 * can actually reach. It carries no cross-tenant affordance and no hint that
 * any data exists behind it.
 */

import React from "react";
import type { ReactNode } from "react";

const panelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 10,
  padding: 24,
  borderRadius: "var(--nu-radius-card)",
  border: "1px solid var(--nu-border)",
  background: "var(--nu-surface)",
  boxShadow: "var(--nu-shadow-card)",
};

export interface PortalPanelStateProps {
  title: ReactNode;
  body?: ReactNode;
  actions?: ReactNode;
  testId: string;
}

function PortalPanelState({
  title,
  body,
  actions,
  testId,
}: PortalPanelStateProps) {
  return (
    <section data-testid={testId} style={panelStyle}>
      <h2 className="nu-h3" style={{ margin: 0 }}>
        {title}
      </h2>
      {body && (
        <p className="nu-body nu-muted" style={{ margin: 0, maxWidth: 640 }}>
          {body}
        </p>
      )}
      {actions}
    </section>
  );
}

/* ── Loading ─────────────────────────────────────────────────────────── */

export interface PortalLoadingStateProps {
  label?: string;
}

export const PortalLoadingState: React.FC<PortalLoadingStateProps> = ({
  label = "Loading your portal…",
}) => (
  <section
    data-testid="portal-loading"
    role="status"
    aria-live="polite"
    style={{ ...panelStyle, alignItems: "center" }}
  >
    <span className="nu-body nu-muted">{label}</span>
  </section>
);

/* ── Error ───────────────────────────────────────────────────────────── */

export interface PortalErrorStateProps {
  message?: string | null;
  onRetry?: () => void;
}

export const PortalErrorState: React.FC<PortalErrorStateProps> = ({
  message,
  onRetry,
}) => (
  <PortalPanelState
    testId="portal-error"
    title="We could not load this right now"
    body={
      message ||
      "The portal service did not respond. Nothing has been changed — try again."
    }
    actions={
      onRetry ? (
        <button type="button" className="nu-btn" onClick={onRetry}>
          Try again
        </button>
      ) : undefined
    }
  />
);

/* ── Empty (teaches the workflow) ────────────────────────────────────── */

export interface PortalEmptyStateProps {
  title: ReactNode;
  /** What this area is for and what happens next — teach, don't just report. */
  teach: ReactNode;
  actions?: ReactNode;
  testId?: string;
}

export const PortalEmptyState: React.FC<PortalEmptyStateProps> = ({
  title,
  teach,
  actions,
  testId = "portal-empty",
}) => (
  <PortalPanelState
    testId={testId}
    title={title}
    body={teach}
    actions={actions}
  />
);

/* ── Permission denied ───────────────────────────────────────────────── */

/**
 * [#698] The server distinguishes two refusals and they need DIFFERENT user
 * actions, so this screen must too:
 *
 *   grant_denied — the tenant's ORGANISATION has not been granted the area.
 *                  Action: ask the NU GUI contact for the grant.
 *   role_denied  — the caller's USER ROLE does not carry portal access
 *                  (`normalizeRole` failed closed on an unratified legacy
 *                  role). Action: a role change from the Tenant Admin. Asking
 *                  NU GUI for a grant would not help, and the grant-worded
 *                  copy sent people to do exactly that.
 *
 * The refusal itself is correct and fail-closed in both cases; only the
 * explanation was misattributed.
 */
export type PortalDeniedReasonKind = "role_denied" | "grant_denied";

export interface PortalDeniedStateProps {
  /** Human name of the area the user tried to reach. */
  areaLabel?: string;
  /** Which refusal this is. Defaults to the page-area grant refusal. */
  reason?: PortalDeniedReasonKind;
  /** Areas the tenant IS granted, offered as the way forward. */
  availableLinks?: readonly { label: string; route: string }[];
  // eslint-disable-next-line no-unused-vars -- param name in a type annotation
  onNavigate?: (route: string) => void;
}

export const PortalDeniedState: React.FC<PortalDeniedStateProps> = ({
  areaLabel,
  reason = "grant_denied",
  availableLinks = [],
  onNavigate,
}) => (
  <section data-testid="portal-denied" data-reason={reason} style={panelStyle}>
    <span
      className="nu-pill nu-pill-warning"
      data-testid="portal-denied-badge"
      style={{ textTransform: "uppercase", letterSpacing: "0.14em" }}
    >
      {reason === "role_denied" ? "No portal access" : "Not granted"}
    </span>
    <h2 className="nu-h3" style={{ margin: 0 }}>
      {reason === "role_denied"
        ? "Your user role does not carry portal access"
        : areaLabel
          ? `${areaLabel} is not part of your portal`
          : "This portal area is not part of your portal"}
    </h2>
    <p className="nu-body nu-muted" style={{ margin: 0, maxWidth: 640 }}>
      {reason === "role_denied"
        ? "This is about your own user account, not about your organisation. " +
          "The lead portal is open to a fixed set of user roles, and yours is " +
          "not one of them — so no grant would open it for you. Ask your " +
          "Tenant Admin to change your role."
        : "NU GUI grants each area of the lead portal to your organisation " +
          "individually. This one has not been granted, so it is not " +
          "available to anyone in your organisation. If you need it, ask " +
          "your NU GUI contact to grant it."}
    </p>
    {availableLinks.length > 0 && (
      <nav aria-label="Areas you can open" style={{ marginTop: 4 }}>
        <p className="nu-tiny nu-subtle" style={{ margin: "0 0 6px" }}>
          Areas you can open
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {availableLinks.map((link) => (
            <button
              key={link.route}
              type="button"
              className="nu-btn nu-btn-ghost"
              onClick={() => onNavigate?.(link.route)}
            >
              {link.label}
            </button>
          ))}
        </div>
      </nav>
    )}
  </section>
);

export default PortalDeniedState;
